import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useRefetch } from "~/hooks/useRefetch"
import { useNewCompanyPaymentMethods } from "./hooks/useNewCompanyPaymentMethods"
import { useBankAccountPendingActions } from "./sepa/hooks/useBankAccountPendingActions"
import { useMandateSign } from "./sepa/hooks/useMandateSign"
import { Heading, Text, Toast, useToast } from "@oysterhr/ui"
import { TransactionsArea } from "~/app/pages/payments/TransactionsArea"
import { companyPaymentMethodsPath } from "@oysterhr/router"
import {
  CompanyCurrencyEnumType,
  Maybe,
  PaymentMethodPurposeEnumType,
  PaymentMethodsCustomerBankAccountSepaType,
  B2BPaymentMethod,
  PaymentMethodState,
  PaymentMethodStripeSepa,
  CorePaymentMethod,
  CustomerBankAccountMandateStateEnumType,
} from "@oysterhr/schema"
import { AddPaymentMethod } from "./components/AddPaymentMethod"
import { WithoutConnectedPaymentMethod } from "./components/WithoutConnectedPaymentMethod"
import { NewCompanyPaymentMethodCard } from "./components/NewCompanyPaymentMethodCard"
import { PendingActions } from "./components/PendingActions"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { PaymentMethodCategoryEnumType } from "./enums"
// alerts
import { AuthorizationAlert } from "./alerts/AuthorizationAlert"
import { SignMandateAlert } from "./alerts/SignMandateAlert"
import { SepaFteVerificationFailureAlert } from "./alerts/SepaFteVerificationFailureAlert"
import { WarningVerifyAlert } from "./alerts/WarningVerifyAlert"
import { VerifyingAlert } from "./alerts/VerifyingAlert"
import { ConfirmMandateSharingAlert } from "./alerts/ConfirmMandateSharingAlert"
import { DeactivationAlert } from "./alerts/DeactivationAlert"
import { SignRequestSentAlert } from "./alerts/SignRequestSentAlert"
import { InfoCreditorsIdAlert } from "./alerts/InfoCreditorsIdAlert"
import { VerificationFailureAlert } from "./alerts/VerificationFailureAlert"
// modals
import { AuthorizeDirectDebitModal } from "./modals/AuthorizeDirectDebitModal"
import { ConfirmMandateSharingModal } from "./modals/ConfirmMandateSharingModal"
import { ResendMandateModal } from "./modals/ResendMandateModal"
import { DeletePaymentMethodModal } from "./modals/DeletePaymentMethodModal"
import { useEffectOnce } from "@oysterhr/common"

const MAX_POLLING_LIMIT = 10

export const NewCompanyPaymentMethodsPage = () => {
  /* Hooks */
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/LandingPage")
  const toast = useToast()
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const pollingAttempts = useRef(0)
  const successToastDisplayedRef = useRef(false)

  // GraphQL
  const { data, loading, refetch, stopPolling } = useNewCompanyPaymentMethods({
    variables: { companyId },
    notifyOnNetworkStatusChange: true,
    pollInterval: 5000,
    onCompleted: data => {
      pollingAttempts.current += 1
      const customerBankAccount = data.company?.customerBankAccount
      // We only poll if mandate hasn't been signed yet and payment has already been authorized
      if (
        customerBankAccount?.state === PaymentMethodState.InVerification ||
        customerBankAccount?.mandate?.state !==
          CustomerBankAccountMandateStateEnumType.Initialized ||
        pollingAttempts.current >= MAX_POLLING_LIMIT
      ) {
        stopPolling()
      }
    },
  })

  useRefetch(loading, refetch)
  const mandateId = data?.company?.customerBankAccount?.mandate?.id
  const mandateSign = useMandateSign(mandateId)

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    CorePaymentMethod | B2BPaymentMethod
  >()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false)
  const [showConfirmSharingModal, setShowConfirmSharingModal] = useState(false)
  const [showResendMandateModal, setShowResendMandateModal] = useState(false)

  /* Derived state */
  const paymentMethod = data?.company?.paymentMethod
  const customerBankAccount = data?.company?.customerBankAccount
  const paymentCurrencyCode = data?.company?.paymentCurrencyCode
  const transactions = data?.company?.transactions

  const onPaymentMethodDelectionSuccess = () => {
    refetch()
  }

  const onAuthorize = (paymentMethod?: Maybe<CorePaymentMethod | B2BPaymentMethod>) => {
    if (!paymentMethod) return
    setSelectedPaymentMethod(paymentMethod)
    setShowAuthorizeModal(true)
  }

  const onDelete = (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod)
    setShowDeleteModal(true)
  }

  const onResend = () => {
    setShowResendMandateModal(true)
  }

  const { pendingActions, completedActions } = useBankAccountPendingActions(
    onAuthorize,
    onResend,
    setShowConfirmSharingModal,
    companyId,
    mandateSign,
    customerBankAccount,
  )

  const reconfirmMandateLodgingSuccessCallback = async () => {
    await refetch()
    setShowConfirmSharingModal(true)
  }

  /* Effects */
  useEffectOnce(() => {
    const stripeCheckoutCompleted =
      location.pathname.split("/").slice(-1)[0] === "processing-checkout"

    if (!stripeCheckoutCompleted || successToastDisplayedRef.current) return

    successToastDisplayedRef.current = true
    toast({
      render: (
        <Toast variation="success">
          <Toast.Title>{t("toasts.connected.title")}</Toast.Title>
          <Toast.Content>{t("toasts.connected.description")}</Toast.Content>
        </Toast>
      ),
    })
    navigate(companyPaymentMethodsPath(companyId), { replace: true })
  })

  const noPaymentMethods = !loading && !paymentMethod && !customerBankAccount && paymentCurrencyCode

  if (noPaymentMethods)
    return (
      <div className="p-8">
        <Heading as="h2" className="font-semibold">
          {t("title")}
        </Heading>
        <Text className="mt-4 text-gray-400">{t("subtitle")}</Text>
        <WithoutConnectedPaymentMethod
          directDebitSupported={data.company.directDebitSupported}
          paymentCurrencyCode={paymentCurrencyCode as CompanyCurrencyEnumType}
        />
      </div>
    )

  // NOTE: We only show the pending actions for SEPA FTE flow */
  const showPendingActions =
    customerBankAccount?.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA &&
    customerBankAccount.state !== PaymentMethodState.AuthorizedForDirectDebit

  return (
    <>
      <div className="p-8">
        <div className="flex items-center justify-between">
          <Heading as="h2" className="font-semibold">
            {t("title")}
          </Heading>
        </div>
        <Text className="mt-4 text-gray-400">{t("subtitle")}</Text>

        {/* Alerts */}
        <div className="mt-4 flex flex-col gap-2">
          {/* Payment method alerts */}
          <InfoCreditorsIdAlert
            paymentMethod={paymentMethod}
            customerBankAccount={customerBankAccount}
          />
          <WarningVerifyAlert paymentMethod={paymentMethod} />
          <DeactivationAlert paymentMethod={paymentMethod} onDelete={onDelete} />
          <VerificationFailureAlert paymentMethod={paymentMethod} onDelete={onDelete} />

          {/* Customer bank account alerts */}
          <AuthorizationAlert paymentMethod={customerBankAccount} onAuthorize={onAuthorize} />
          <SignMandateAlert paymentMethod={customerBankAccount} mandateSign={mandateSign} />
          <SignRequestSentAlert paymentMethod={customerBankAccount} onResend={onResend} />
          <VerificationFailureAlert paymentMethod={customerBankAccount} onDelete={onDelete} />
          <VerifyingAlert paymentMethod={customerBankAccount} />
          <ConfirmMandateSharingAlert
            companyId={companyId}
            paymentMethod={customerBankAccount}
            onConfirm={() => setShowConfirmSharingModal(true)}
          />
          <SepaFteVerificationFailureAlert
            customerBankAccount={customerBankAccount}
            onDelete={onDelete}
            reconfirmMandateLodgingSuccessCallback={reconfirmMandateLodgingSuccessCallback}
          />
        </div>

        <div className="mt-6 grid grid-cols-2 grid-rows-2 gap-8">
          {customerBankAccount && (
            <>
              <div className="col-span-1 row-span-1">
                <NewCompanyPaymentMethodCard
                  key={customerBankAccount.id}
                  companyId={companyId}
                  paymentMethod={customerBankAccount}
                  onDelete={onDelete}
                  onAuthorize={onAuthorize}
                  setShowConfirmSharingModal={setShowConfirmSharingModal}
                  mandateSign={mandateSign}
                />
              </div>
              {showPendingActions ? (
                <div className="row-span-2">
                  <PendingActions pendingTasks={pendingActions} completedTasks={completedActions} />
                </div>
              ) : null}
            </>
          )}

          {paymentMethod && (
            <NewCompanyPaymentMethodCard
              key={paymentMethod.id}
              companyId={companyId}
              paymentMethod={paymentMethod as PaymentMethodStripeSepa}
              onDelete={onDelete}
              onAuthorize={onAuthorize}
            />
          )}
          {paymentMethod &&
            // NOTE: When purpose ois "collection_and_passing", it means it is used for FTE & contractor.
            // ie was added in the old flow. so we dont allow them to add FTE payment method
            // NOTE: purpose "passing" means its only for contractor
            paymentMethod.purpose === PaymentMethodPurposeEnumType.Passing &&
            !customerBankAccount && (
              <AddPaymentMethod
                paymentCurrencyCode={paymentCurrencyCode}
                paymentMethodType={PaymentMethodCategoryEnumType.Fte}
              />
            )}
          {customerBankAccount && !paymentMethod && (
            <AddPaymentMethod
              paymentCurrencyCode={paymentCurrencyCode}
              paymentMethodType={PaymentMethodCategoryEnumType.Contractor}
            />
          )}

          {paymentMethod && !!transactions?.length && (
            <div className="w-1/2">
              <TransactionsArea transactions={transactions} />
            </div>
          )}
        </div>
      </div>

      {/* ---- Modals ---- */}
      <DeletePaymentMethodModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        paymentMethod={selectedPaymentMethod}
        onDeletionSuccess={onPaymentMethodDelectionSuccess}
      />
      <AuthorizeDirectDebitModal
        isOpen={showAuthorizeModal}
        onClose={() => setShowAuthorizeModal(false)}
        paymentMethod={selectedPaymentMethod}
      />
      <ConfirmMandateSharingModal
        isOpen={showConfirmSharingModal}
        onClose={() => setShowConfirmSharingModal(false)}
        customerBankAccount={customerBankAccount as PaymentMethodsCustomerBankAccountSepaType}
      />
      <ResendMandateModal
        isOpen={showResendMandateModal}
        onClose={() => setShowResendMandateModal(false)}
        customerBankAccount={customerBankAccount as PaymentMethodsCustomerBankAccountSepaType}
      />
    </>
  )
}
